import { computed } from 'vue'

/**
 * 版本信息接口
 */
export interface AppVersionInfo {
  /** 应用版本号 (来自package.json) */
  version: string
  /** Git提交哈希值 */
  gitCommitHash: string
  /** Git分支名 */
  gitBranch: string
  /** 构建时间 */
  buildTime: string
  /** 构建模式 */
  buildMode: string
  /** 格式化的构建时间 */
  buildTimeFormatted: string
  /** 版本摘要信息 */
  versionSummary: string
}

/**
 * 版本信息组合式函数
 */
export function useAppVersion() {
  const versionInfo = computed<AppVersionInfo>(() => {
    const buildTime = new Date(__BUILD_TIME__)

    return {
      version: __APP_VERSION__,
      gitCommitHash: __GIT_COMMIT_HASH__,
      gitBranch: __GIT_BRANCH__,
      buildTime: __BUILD_TIME__,
      buildMode: __BUILD_MODE__,
      buildTimeFormatted: buildTime.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }),
      versionSummary: `v${__APP_VERSION__} (${__GIT_COMMIT_HASH__})`
    }
  })

  /**
   * 获取版本信息
   */
  const getVersionInfo = () => versionInfo.value

  /**
   * 获取版本摘要
   */
  const getVersionSummary = () => versionInfo.value.versionSummary

  /**
   * 获取详细的版本信息文本
   */
  const getVersionDetails = () => {
    const info = versionInfo.value
    return [
      `版本: ${info.version}`,
      `提交: ${info.gitCommitHash}`,
      `分支: ${info.gitBranch}`,
      `构建: ${info.buildTimeFormatted}`,
      `模式: ${info.buildMode}`
    ].join('\n')
  }

  /**
   * 复制版本信息到剪贴板
   */
  const copyVersionInfo = async () => {
    try {
      await navigator.clipboard.writeText(getVersionDetails())
      return true
    } catch (error) {
      console.error('复制版本信息失败:', error)
      return false
    }
  }

  /**
   * 在控制台打印版本信息
   */
  const logVersionInfo = () => {
    const info = versionInfo.value
    console.group('应用版本信息')
    console.log('版本号:', info.version)
    console.log('Git提交:', info.gitCommitHash)
    console.log('Git分支:', info.gitBranch)
    console.log('构建时间:', info.buildTimeFormatted)
    console.log('构建模式:', info.buildMode)
    console.groupEnd()
  }

  return {
    versionInfo,
    getVersionInfo,
    getVersionSummary,
    getVersionDetails,
    copyVersionInfo,
    logVersionInfo
  }
}
